import { useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import { useTranslation } from 'react-i18next';

import { ScrollArea } from '@app/shared/ui/atoms/Common';
import {
  AboutLabel,
  BottomBar,
  NameSection,
  PowerCard,
  TopBar,
} from '@app/shared/ui/molecules/Profile';
import { useGetUserProfileQuery } from '@app/store/slices/users/usersApi';
import { useGetUserBalanceQuery } from '@app/store/slices/balance/balanceApi';
import { useGetUserProgressQuery } from '@app/store/slices/userProgress/userProgressApi';
import { useDynamicTranslationNamespace } from '@app/shared/hooks/useDynamicTranslationNamespace';
import { useDisplayError } from '@app/shared/hooks/useDisplayError';
import { getTags } from '@app/shared/utils/userProfile';

export default function UserProfilePage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const userId = id ? Number(id) : undefined;

  const {
    data: user,
    isLoading: isUserLoading,
    isError: isUserError,
  } = useGetUserProfileQuery(userId ?? skipToken);
  const { data: userBalance, isError: isUserBalanceError } = useGetUserBalanceQuery(
    userId ?? skipToken
  );
  const { data: userProgress, isError: isUserProgressError } = useGetUserProgressQuery(
    userId ?? skipToken
  );

  useDynamicTranslationNamespace('countries');

  useDisplayError(
    isUserError || isUserBalanceError || isUserProgressError,
    t('errors.failed_to_load_user')
  );

  const userLevel = userProgress?.level ? userProgress.level + 1 : 1;
  const tonTotalIncome = Number(userBalance?.tonTotalIncome ?? 0);
  const tags = getTags(user?.country, user?.city, user?.activitiesScope, t);

  return (
    <ScrollArea className="gap-4">
      <TopBar isMyProfile={false} isSuperUser={false} />
      <NameSection
        name={user?.fullName}
        avatar={user?.avatar}
        level={userLevel}
        peopleCount={user?.refUsersCount}
        diamondsCount={tonTotalIncome}
        isUserLoading={isUserLoading}
      />
      <PowerCard
        title={user?.superPower?.title}
        description={user?.superPower?.data}
        showPower={user?.showSuperPower ?? true}
      />
      {user?.bio && <AboutLabel text={user.bio} />}
      <BottomBar tags={tags} instagram={user?.instagramTag} telegram={user?.telegramUsername} />
    </ScrollArea>
  );
}
